import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  Loader,
  Badge,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconDatabase, IconLogin } from '@tabler/icons-react';
import { setAuthToken } from '../lib/api';
import { getArchimageLogoUrl } from '../lib/brandAssets';
import LoginHeroCarousel from './LoginHeroCarousel';
import classes from './LoginPage.module.css';

export default function LoginPage({ onLogin }) {
  const logoUrl = getArchimageLogoUrl();
  const logoMask = {
    WebkitMaskImage: `url(${logoUrl})`,
    maskImage: `url(${logoUrl})`,
  };
  const [loading, setLoading] = useState(false);
  const [dbStatus, setDbStatus] = useState({ checking: true, ok: false });

  const form = useForm({
    initialValues: { login: '', password: '' },
    validate: {
      login: (v) => (v.trim().length > 0 ? null : 'Identifiant requis'),
      password: (v) => (v.length > 0 ? null : 'Mot de passe requis'),
    },
  });

  useEffect(() => {
    fetch('/api/ping')
      .then((r) => r.json())
      .then((d) => setDbStatus({ checking: false, ok: Boolean(d.success) }))
      .catch(() => setDbStatus({ checking: false, ok: false }));
  }, []);

  const handleSubmit = form.onSubmit(async (values) => {
    setLoading(true);
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (data.success) {
        setAuthToken(data.token);
        notifications.show({
          title: 'Bienvenue',
          message: data.user.prenom
            ? `${data.user.prenom} ${data.user.nom}`
            : data.user.login,
          color: 'blue',
        });
        onLogin(data.user);
      } else {
        notifications.show({
          title: 'Connexion refusée',
          message: data.message || 'Erreur',
          color: 'red',
        });
      }
    } catch {
      notifications.show({
        title: 'Erreur',
        message: 'Impossible de joindre le serveur',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  });

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.pageBrand} aria-label="Archimage">
        <Box className={classes.pageBrandInner}>
          <Box className={classes.pageBrandLogoWrap}>
            <Box className={classes.pageBrandLogoWhite} style={logoMask} aria-hidden="true" />
            <img src={logoUrl} alt="Archimage" className={classes.pageBrandLogo} />
          </Box>
        </Box>
      </Box>

      <Box className={classes.hero}>
        <LoginHeroCarousel />
      </Box>

      <Box className={classes.formSide}>
        <Paper className={classes.formCard} radius="lg" p="xl" shadow="md" withBorder>
          <Stack gap="lg">
            <div>
              <Title order={2} size="h3">
                Connexion
              </Title>
              <Text c="dimmed" size="sm" mt={4}>
                Identifiez-vous pour accéder à l&apos;application
              </Text>
            </div>

            <Group gap="xs">
              <IconDatabase size={16} />
              {dbStatus.checking ? (
                <Loader size="xs" />
              ) : (
                <>
                  <span
                    className={classes.statusDot}
                    style={{ background: dbStatus.ok ? '#12b886' : '#fa5252' }}
                  />
                  <Text size="xs" c="dimmed">
                    {dbStatus.ok ? 'Base de données connectée' : 'Base de données indisponible'}
                  </Text>
                </>
              )}
            </Group>

            {!dbStatus.checking && !dbStatus.ok && (
              <Alert color="orange" variant="light" title="Configuration">
                Créez la base <strong>archimage</strong>, copiez <code>.env.example</code> vers{' '}
                <code>.env</code>, puis <code>npm run migrate</code> et <code>npm run seed</code>.
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                <TextInput
                  label="Identifiant"
                  placeholder="Identifiant "
                  radius="md"
                  size="md"
                  autoComplete="username"
                  {...form.getInputProps('login')}
                />
                <PasswordInput
                  label="Mot de passe"
                  placeholder="••••••••"
                  radius="md"
                  size="md"
                  autoComplete="current-password"
                  {...form.getInputProps('password')}
                />
                <Button
                  type="submit"
                  size="md"
                  radius="md"
                  loading={loading}
                  leftSection={<IconLogin size={18} />}
                  fullWidth
                  color="blue"
                >
                  Se connecter
                </Button>
              </Stack>
            </form>

            <Badge variant="light" color="gray" size="sm" style={{ alignSelf: 'center' }}>
              ARCHIMAGE · React · Mantine
            </Badge>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
